import {
    EXPERT_ERROR_LOG_COUNTER,
    EXPERT_ERROR_LOG_REQUEST_FAILED,
    POPD_PROTOCOL_COUNTER,
} from "../../Controls/metricsAction";
import { track } from "../../Controls/Internals/Metrics/MetricsProvider";
import { getCandyHub } from "../../Forms/Sentry/InitializeSentry";
import { BindingPath } from "../Core/CoreTypes";
import { ErrorLogAction, ErrorLogActionsType } from "../Core/ErrorManager/Store/ErrorLogActions";
import { CandyDispatch, CandyThunkAction } from "../Core/Store/CandyReduxTypes";
import { IForm } from "../Form/IForm";
import { ICandyComponentHandle } from "../Core/ComponentHandleRegistry/ICandyComponentHandle";
import { stringifyUnknownError } from "../../../../Common/TypingUtils";
import { parseErrorLog, serverErrorLogToGroupedAdapter } from "../Core/ErrorManager/Utils/Utils";
import { ErrorLog } from "../Core/ErrorManager/Utils/Models";
import { CandyPopdProtocol } from "../../../../Common/CandyApiClient/Types/ErrorLog/CandyPopdProtocol";
import { NavigationPathMapper } from "../Core/PathUtils/NavigationPathMapper";
import { AttachmentErrorLog } from "../../../../Common/CandyApiClient/Types/ErrorLog/AttachmentErrorLog";
import { IPublicForm } from "../../Forms/PublicForm/IPublicForm";
import { getAuoOptions } from "../Core/Store/CandyStoreUtils";

export function changeErrorLogModelStateAction(modelChanged: boolean): ErrorLogAction {
    return {
        type: ErrorLogActionsType.UpdateModelState,
        payload: {
            modelChanged: modelChanged,
        },
    };
}

export function requestErrorLog(): ErrorLogAction {
    return { type: ErrorLogActionsType.requestErrorlog };
}

export function receivedErrorlog(errorLog: ErrorLog): ErrorLogAction {
    return {
        type: ErrorLogActionsType.receivedErrorLog,
        payload: errorLog,
    };
}

export function requestFailed(): ErrorLogAction {
    return { type: ErrorLogActionsType.requestFailed };
}

export function requestExpertErrorLog(): ErrorLogAction {
    return { type: ErrorLogActionsType.requestExpertErrorlog };
}

export function receivedExpertErrorlog(errorLog: ErrorLog): ErrorLogAction {
    return {
        type: ErrorLogActionsType.receivedExpertErrorLog,
        payload: errorLog,
    };
}

export function requestExpertErrorLogFailed(): ErrorLogAction {
    return {
        type: ErrorLogActionsType.requestExpertErrorLogFailed,
    };
}

export function requestPopdProtocol(): ErrorLogAction {
    return { type: ErrorLogActionsType.requestPopdProtocol };
}

export function receivedPopdProtocol(errorLog: CandyPopdProtocol): ErrorLogAction {
    return {
        type: ErrorLogActionsType.receivedPopdProtocol,
        payload: errorLog,
    };
}

export function requestPopdProtocolFailed(): ErrorLogAction {
    return { type: ErrorLogActionsType.requestPopdProtocolFailed };
}

export const checkByPopd: CandyThunkAction<Promise<void>> = async (dispatch, _, form): Promise<void> => {
    dispatch(requestPopdProtocol());
    try {
        await form.changeSet.send();

        const popdProtocol = await form.candyApiDraftClient.checkByPopd();

        track("candy", POPD_PROTOCOL_COUNTER, {
            resultCode: popdProtocol.resultCode,
            errors: popdProtocol.errorsCount,
            warnings: popdProtocol.warningsCount,
        });

        dispatch(receivedPopdProtocol(popdProtocol));
    } catch (e) {
        dispatch(requestPopdProtocolFailed());
        // eslint-disable-next-line no-console
        console.error(e);
    }
};

export const getExpertErrorLog: CandyThunkAction<Promise<void>> = async (dispatch, getState, form) => {
    dispatch(requestExpertErrorLog());
    try {
        await form.changeSet.send();
        const noPathMapping = form.lazyLoadingManager.isLazyLoadingEnabled();
        const auoOptions = getAuoOptions(getState());
        const expertErrorLog = await form.candyApiDraftClient.getExpertErrorLog(auoOptions);
        const errorLogWithoutLinks = parseErrorLog(
            serverErrorLogToGroupedAdapter(expertErrorLog),
            () => form.navigationPathMapper,
            true,
            noPathMapping
        );
        track("candy", EXPERT_ERROR_LOG_COUNTER, {
            errors: expertErrorLog.errors.length,
            warnings: expertErrorLog.warnings.length,
        });

        dispatch(receivedExpertErrorlog(errorLogWithoutLinks));
    } catch (e) {
        track("candy", EXPERT_ERROR_LOG_REQUEST_FAILED, { error: stringifyUnknownError(e) });
        dispatch(requestExpertErrorLogFailed());
        // eslint-disable-next-line no-console
        console.error(e);
    }
};

export const getErrorLog: CandyThunkAction<Promise<void>> = async (
    dispatch: CandyDispatch,
    getState,
    form: IForm
): Promise<void> => {
    function getPathMapperForGroup(groupAttachmentId?: string): NavigationPathMapper {
        if (groupAttachmentId) {
            const attachmentPublicForms = form.attachmentsPublicForms[groupAttachmentId];
            if (attachmentPublicForms) {
                return attachmentPublicForms.getNavigationPathMapper();
            }
        }
        return form.navigationPathMapper;
    }

    dispatch(requestErrorLog());
    try {
        await form.changeSet.send();
        await form.normalizeDirtyMasksAndReloadInnerIfRequired();
        const noPathMapping = form.lazyLoadingManager.isLazyLoadingEnabled();
        const errorLog = await form.errorLogApi.getErrorLog(attId => getState().attachments.byId[attId]);
        dispatch(receivedErrorlog(parseErrorLog(errorLog, getPathMapperForGroup, false, noPathMapping)));
    } catch (e) {
        if (sessionStorage.getItem("FakeErrorLog." + form.settings.draftId) == undefined) {
            // Пропустим ошибку ибо сюда бывает подсовывают ошибочный error log
            getCandyHub().captureException(e);
        }
        form.errorLogApi.resetStatuses();
        dispatch(requestFailed());
        // eslint-disable-next-line no-console
        console.error(e);
    }
};

export function setAttachmentErrorLog(attachmentErrorLog: AttachmentErrorLog): ErrorLogAction {
    return {
        type: ErrorLogActionsType.setAttachmentErrors,
        payload: attachmentErrorLog,
    };
}

export function clearAttachmentErrorLog(): ErrorLogAction {
    return {
        type: ErrorLogActionsType.clearAttachmentErrors,
    };
}

export function navigateInsideAttachment(
    attachmentPublicForm: IPublicForm,
    highlanderPath: string
): CandyThunkAction<Promise<undefined | ICandyComponentHandle>> {
    return () => attachmentPublicForm.navigateByHighlanderPaths([highlanderPath]);
}

export function navigateToAttachment(
    attachmentId: string,
    highlanderPath: string
): CandyThunkAction<Promise<undefined | ICandyComponentHandle>> {
    return (_, __, form) => {
        const attachmentPublicForm = form.getAttachmentsPublicFormsById(attachmentId);

        return attachmentPublicForm.navigateByHighlanderPaths([highlanderPath]);
    };
}

export function navigateByBindingPathsOrHighlanderPathActionCreator(
    attachmentId?: string
): CandyThunkAction<
    (
        paths: BindingPath[],
        highlanderPath?: string | null,
        errorItemId?: string
    ) => Promise<undefined | ICandyComponentHandle>
> {
    return (dispatch, getState, form) => {
        if (attachmentId) {
            const attachmentInfo = getState().attachments.byId[attachmentId];
            const attachmentPath = attachmentInfo?.meta.bindingPath;

            if (attachmentPath) {
                return async (_, highlanderPath, errorItemId) => {
                    if (highlanderPath) {
                        dispatch(
                            setAttachmentErrorLog({
                                attachmentId: attachmentId,
                                path: highlanderPath,
                                errorItemId: errorItemId,
                            })
                        );
                    }

                    await form.activeLogHelper.navigateByHighlanderPaths([attachmentPath]);

                    return undefined;
                };
            }
        }

        return (paths: BindingPath[]) =>
            form.lazyLoadingManager.isLazyLoadingEnabled()
                ? form.activeLogHelper.navigateByHighlanderPaths(paths)
                : form.activeLogHelper.navigateByBindingPaths(paths);
    };
}
